/**
 * Sticky Add to Cart Bar - Web Component
 * 
 * Displays a sticky bar at the bottom of product pages that:
 * - Shows after cart drawer closes and user scrolls down
 * - Hides when user scrolls back up near the main product form
 * - Syncs with the main product form's selected variant
 * - Uses theme's existing add-to-cart logic
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    
    // State management
    this.isVisible = false;
    this.cartDrawerClosed = true; // Allow showing by default (standard sticky behavior)
    this.scrollThreshold = 300; // Pixels to scroll before showing
    this.mainProductForm = null;
    this.productFormObserver = null;
    this.lastScrollY = 0;
    this.ticking = false;
  }

  connectedCallback() {
    // Only initialize on product pages
    if (!this.isProductPage()) {
      this.remove();
      return;
    }

    // Find the main product form
    this.mainProductForm = document.querySelector('form[action*="/cart/add"]') || 
                           document.querySelector('form[data-product-form]');
    
    if (!this.mainProductForm) {
      console.warn('Sticky Add to Cart: Main product form not found');
      return;
    }

    // Initialize component
    this.setupEventListeners();
    this.setupScrollListener();
    this.setupCartDrawerListener();
    this.setupVariantSync();
    
    // Initial state - hidden
    this.hide(false);
  }

  disconnectedCallback() {
    // Cleanup
    window.removeEventListener('scroll', this.handleScroll);
    if (this.productFormObserver) {
      this.productFormObserver.disconnect();
    }
  }

  /**
   * Check if we're on a product page
   */
  isProductPage() {
    return document.body.classList.contains('template-product') || 
           document.querySelector('[data-section-type="product"]');
  }

  /**
   * Setup scroll listener with requestAnimationFrame for performance
   */
  setupScrollListener() {
    this.handleScroll = () => {
      this.lastScrollY = window.scrollY;
      
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.checkScrollPosition();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  /**
   * Check scroll position and show/hide bar accordingly
   */
  checkScrollPosition() {
    // Don't show until cart drawer has been closed at least once
    if (!this.cartDrawerClosed) {
      return;
    }

    const scrollY = this.lastScrollY;
    const mainForm = this.mainProductForm;
    
    if (!mainForm) return;

    // Get the position of the main product form
    const formRect = mainForm.getBoundingClientRect();
    const formBottom = formRect.bottom;
    
    // Show bar when:
    // 1. User has scrolled past the threshold
    // 2. Main product form is above the viewport
    const shouldShow = scrollY > this.scrollThreshold && formBottom < 0;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Listen for cart drawer close events
   */
  setupCartDrawerListener() {
    // Listen for cart drawer close
    // This works with most Shopify themes that dispatch custom events
    document.addEventListener('cart:close', () => {
      this.cartDrawerClosed = true;
    });

    // Fallback: Watch for cart drawer element changes
    const observer = new MutationObserver(() => {
      const cartDrawer = document.querySelector('[data-cart-drawer]') || 
                        document.querySelector('.cart-drawer') ||
                        document.querySelector('#cart-drawer');
      
      if (cartDrawer) {
        const isOpen = cartDrawer.classList.contains('is-open') || 
                      cartDrawer.classList.contains('active') ||
                      cartDrawer.hasAttribute('open');
        
        if (!isOpen && !this.cartDrawerClosed) {
          this.cartDrawerClosed = true;
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'open']
    });

    // Also mark as closed if user clicks outside or presses escape
    document.addEventListener('click', (e) => {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer && !cartDrawer.contains(e.target)) {
        setTimeout(() => {
          this.cartDrawerClosed = true;
        }, 300);
      }
    });
  }

  /**
   * Setup variant synchronization with main product form
   */
  setupVariantSync() {
    // Listen for variant change events
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateVariant(e.detail.variant);
      }
    });

    // Watch for changes in the main form
    if (this.mainProductForm) {
      // Listen to variant selector changes
      const variantSelect = this.mainProductForm.querySelector('[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          this.syncVariantFromForm();
        });
      }

      // Watch for variant option changes (swatches, dropdowns, etc.)
      const variantInputs = this.mainProductForm.querySelectorAll('input[name^="option"], select[name^="option"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          setTimeout(() => this.syncVariantFromForm(), 100);
        });
      });

      // Use MutationObserver to watch for price changes
      const priceElement = document.querySelector('[data-product-price]');
      if (priceElement) {
        this.productFormObserver = new MutationObserver(() => {
          this.syncVariantFromForm();
        });
        
        this.productFormObserver.observe(priceElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }

    // Initial sync
    this.syncVariantFromForm();
  }

  /**
   * Sync variant data from the main product form
   */
  syncVariantFromForm() {
    const variantIdInput = this.mainProductForm.querySelector('[name="id"]');
    const priceElement = document.querySelector('[data-product-price]');
    const compareAtPriceElement = document.querySelector('[data-compare-price]');
    
    if (variantIdInput) {
      const variantId = variantIdInput.value;
      const stickyVariantInput = this.querySelector('[name="id"]');
      
      if (stickyVariantInput) {
        stickyVariantInput.value = variantId;
      }

      // Update price
      if (priceElement) {
        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice) {
          stickyPrice.innerHTML = priceElement.innerHTML;
        }
      }

      // Check availability
      const addButton = this.querySelector('[data-sticky-add-to-cart]');
      const mainAddButton = this.mainProductForm.querySelector('[name="add"]') ||
                           this.mainProductForm.querySelector('[type="submit"]');
      
      if (addButton && mainAddButton) {
        const isDisabled = mainAddButton.disabled || 
                          mainAddButton.classList.contains('disabled') ||
                          mainAddButton.hasAttribute('disabled');
        
        addButton.disabled = isDisabled;
        
        if (isDisabled) {
          addButton.textContent = mainAddButton.textContent || 'Sold Out';
        } else {
          addButton.textContent = 'Add to Cart';
        }
      }
    }
  }

  /**
   * Update variant information
   */
  updateVariant(variant) {
    const variantInput = this.querySelector('[name="id"]');
    const priceElement = this.querySelector('[data-sticky-price]');
    const addButton = this.querySelector('[data-sticky-add-to-cart]');

    if (variantInput) {
      variantInput.value = variant.id;
    }

    if (priceElement && variant.price) {
      // Format price (assuming Shopify money format)
      const price = this.formatMoney(variant.price);
      priceElement.textContent = price;
    }

    if (addButton) {
      if (variant.available) {
        addButton.disabled = false;
        addButton.textContent = 'Add to Cart';
      } else {
        addButton.disabled = true;
        addButton.textContent = 'Sold Out';
      }
    }
  }

  /**
   * Format money (basic implementation)
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Setup event listeners for the sticky bar
   */
  setupEventListeners() {
    const addButton = this.querySelector('[data-sticky-add-to-cart]');
    
    if (addButton) {
      addButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }
  }

  /**
   * Handle add to cart action
   */
  async handleAddToCart() {
    const form = this.querySelector('form');
    const addButton = this.querySelector('[data-sticky-add-to-cart]');
    
    if (!form || !addButton) return;

    // Get form data
    const formData = new FormData(form);
    
    // Show loading state
    const originalText = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = 'Adding...';

    try {
      // Use Shopify's cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Trigger cart update event (theme-specific)
      document.dispatchEvent(new CustomEvent('cart:add', { 
        detail: { item: data } 
      }));

      // Open cart drawer (theme-specific)
      const cartDrawerTrigger = document.querySelector('[data-cart-drawer-trigger]') ||
                               document.querySelector('[href="/cart"]') ||
                               document.querySelector('.cart-link');
      
      if (cartDrawerTrigger) {
        cartDrawerTrigger.click();
      }

      // Dispatch a generic event that themes might listen to
      document.dispatchEvent(new CustomEvent('product:added-to-cart'));
      
      // Try to open the cart drawer using common methods
      if (window.theme && window.theme.cart && window.theme.cart.open) {
        window.theme.cart.open();
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Sorry, there was an error adding this item to your cart. Please try again.');
    } finally {
      // Reset button state
      addButton.disabled = false;
      addButton.textContent = originalText;
    }
  }

  /**
   * Show the sticky bar with animation
   */
  show(animate = true) {
    this.isVisible = true;
    this.classList.add('is-visible');
    
    if (!animate) {
      this.style.transition = 'none';
      setTimeout(() => {
        this.style.transition = '';
      }, 50);
    }
  }

  /**
   * Hide the sticky bar with animation
   */
  hide(animate = true) {
    this.isVisible = false;
    this.classList.remove('is-visible');
    
    if (!animate) {
      this.style.transition = 'none';
      setTimeout(() => {
        this.style.transition = '';
      }, 50);
    }
  }
}

// Register the custom element
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
